import { useState } from "react";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";

export interface AudioEventData {
  name?: string;
  data?: {
    path?: string;
    interruptible?: boolean;
    loop?: boolean;
  };
  timestamp?: string;
  turn?: number;
  background?: boolean;
  background_task_name?: string | null;
}

const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  } catch {
    return timestamp;
  }
};

// Content component for the Audio tab
const AudioContent = ({ data }: { data: AudioEventData }) => {
  return (
    <View className="space-y-4">
      {/* Audio Path */}
      {data.data?.path && (
        <View className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
          <Text className="text-sm text-gray-800 dark:text-gray-200 font-mono break-all">{data.data.path}</Text>
        </View>
      )}

      {/* Settings */}
      <View className="flex flex-row flex-wrap gap-4">
        {/* Loop */}
        {data.data?.loop !== undefined && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name={data.data.loop ? "repeat" : "close"}
              size={14}
              className={data.data.loop ? "text-purple-500" : "text-gray-400"}
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">Loop</Text>
          </View>
        )}

        {/* Interruptible */}
        {data.data?.interruptible !== undefined && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name={data.data.interruptible ? "check" : "close"}
              size={14}
              className={data.data.interruptible ? "text-green-500" : "text-gray-400"}
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">Interruptible</Text>
          </View>
        )}

        {/* Timestamp */}
        {data.timestamp && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="clock"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">{formatTimestamp(data.timestamp)}</Text>
          </View>
        )}

        {/* Background */}
        {data.background && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="layers"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">Background{data.background_task_name ? `: ${data.background_task_name}` : ""}</Text>
          </View>
        )}
      </View>
    </View>
  );
};

// Main AudioEventSpanDetailView component with tabs
interface AudioEventSpanDetailViewProps {
  data: AudioEventData;
  rawData: RawSpanData;
  className?: string;
}

const tabs: TabDefinition[] = [
  { id: "audio", label: "Audio", color: "indigo" },
  { id: "attributes", label: "Attributes", color: "gray" },
];

export const AudioEventSpanDetailView = ({ data, rawData, className }: AudioEventSpanDetailViewProps) => {
  const [activeTab, setActiveTab] = useState("audio");

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <View className="flex-1 overflow-y-auto p-4">{activeTab === "audio" ? <AudioContent data={data} /> : <RawSpanDetailView data={rawData} />}</View>
    </View>
  );
};

// Helper to parse audio event data from span attributes
export const parseAudioEventData = (attributes: Record<string, unknown>): AudioEventData | null => {
  const outputValue = attributes["output.value"];
  if (typeof outputValue !== "string") return null;

  try {
    const parsed = JSON.parse(outputValue);
    return parsed as AudioEventData;
  } catch {
    return null;
  }
};
